import React, { useRef, useState } from 'react';
import { Gallery } from 'react-photoswipe-gallery';
import TextareaAutosize from 'react-textarea-autosize';

import { Box, CircularProgress } from '@mui/material';

import styled from '@emotion/styled';
import { useMutation } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';

import { updateMessagesStatusBtwVenueAndUserApi } from '../../../api/chat/updateMessagesStatusBtwVenueAndUserApi';
import { venueSendChatMessageToUserApi } from '../../../api/chat/venueSendChatMessageToUserApi';
import { uploadFileToS3 } from '../../../api/files/uploadFileToS3';
import { ChatMessageItem, ConversationItem, ShippingStatus } from '../../../graphql/API';
import { useUserChatMessageListenerEvent } from '../../../hooks/reactQuery/chat/useUserChatMessageListenerEvent';
import { useVenueListConversationsQuery } from '../../../hooks/reactQuery/chat/useVenueListConversationsQuery';
import { useChatListQuery } from '../../../hooks/reactQuery/useChatListQuery';
import { useMessageSeenListenerQuery } from '../../../hooks/reactQuery/useMessageSeenListenerQuery';
import { useQueryReservationById } from '../../../hooks/reactQuery/useQueryReservationById';
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll';
import useVenueId from '../../../hooks/useVenueId';
import { useAppSelector } from '../../../redux/hooks';
import Icons from '../../../themes/icons';
import { AttachmentInput, FileType } from '../../ChatSupport/AttachmentInput/AttachmentInput';
import { MessageV2 } from '../../ChatSupport/MessageV2/MessageV2';
import { ChatHeader } from '../ChatHeader';
import { ReservationRequirement } from '../ReservationRequirement';
import { ViolationMessage } from '../ViolationMessage';

type Props = {
  conversation: ConversationItem;
  setRefreshConversation: React.Dispatch<React.SetStateAction<number>>;
};

export const Chat: React.FC<Props> = ({ conversation, setRefreshConversation }) => {
  const venueState = useAppSelector((state) => state.venue.value);

  const { enqueueSnackbar } = useSnackbar();
  const venueID = useVenueId();
  const messageInputRef = useRef<HTMLTextAreaElement>(null);
  const scrollingView = useRef<HTMLDivElement | null>(null);

  const [newMessage, setNewMessage] = useState('');
  const [messages, setMessages] = React.useState<Nullable<ChatMessageItem | undefined>[]>([]);
  const [isFileUploading, setIsFileUploading] = useState(false);
  const [isLocalMessageStart, setIsLocalMessageStart] = useState(false);

  const { data: reservationDetail, refetch: reservationDetailsRefetch } = useQueryReservationById(
    conversation.reservationID || '',
  );

  const { revalidateConversations } = useVenueListConversationsQuery({ venueID });

  const [, toUserId] = conversation.toUserId.split('-');
  const { message: newMessageListener } = useUserChatMessageListenerEvent(
    toUserId || '',
    venueID || '',
  );

  const { updatedMessage } = useMessageSeenListenerQuery({
    conversationId: conversation?.conversationId || '',
  });

  const {
    isFetching: isFetchingMessages,
    isLoading: isLoadingMessages,
    data,
    hasNextPage,
    fetchNextPage,
    revalidateChatList,
  } = useChatListQuery({ conversationId: conversation?.conversationId || '' });

  const postMessageMutation = useMutation({
    mutationFn: venueSendChatMessageToUserApi,
    onSuccess: () => {
      console.log('sended');
      revalidateChatList();
      setRefreshConversation((prev: number) => prev + 1);
    },
    onSettled: () => {
      setIsLocalMessageStart(true);
    },
    onError: () => {
      enqueueSnackbar('Something went wrong while sending the message', { variant: 'error' });
    },
  });

  const updateMessagesStatusMutation = useMutation({
    mutationFn: updateMessagesStatusBtwVenueAndUserApi,
    onSettled: () => {},
    onError: () => {
      enqueueSnackbar('Something went wrong while updating the message status.', {
        variant: 'error',
      });
    },
  });

  const { lastElementRef } = useInfiniteScroll({
    isFetching: isFetchingMessages || isLoadingMessages,
    hasMore: hasNextPage,
    cb: () => {
      fetchNextPage();
      setIsLocalMessageStart(false);
    },
  });

  const focusInput = React.useCallback(() => {
    if (messageInputRef.current) {
      messageInputRef.current.focus();
    }
  }, []);

  const handleMessageUpdate = async (messages: Nullable<ChatMessageItem | undefined>[]) => {
    const _unseenMessages = messages.filter(
      (message) => message && message.toUserId === venueID && !message.seen,
    );
    if (_unseenMessages.length > 0) {
      await updateMessagesStatusMutation.mutateAsync(
        _unseenMessages.map((message) => ({
          messageTime: message?.messageTime || '',
          conversationId: message?.conversationId || '',
          seen: true,
          received: true,
        })),
      );
    }
  };

  React.useEffect(() => {
    if (messages.length > 0) {
      handleMessageUpdate(messages);
    }
  }, [conversation.conversationId, messages]);

  React.useEffect(() => {
    if (newMessageListener) {
      setMessages((pre) => [newMessageListener, ...pre]);
    }
  }, [newMessageListener]);

  React.useEffect(() => {
    if (newMessageListener) {
      revalidateConversations();
    }
  }, [newMessageListener]);

  React.useEffect(() => {
    focusInput();
  }, [focusInput]);

  React.useEffect(() => {
    setMessages(data.filter((message) => message?.messageType !== 'shipment'));
  }, [data]);

  React.useEffect(() => {
    if (updatedMessage) {
      revalidateChatList();
    }
  }, [updatedMessage]);

  const handleFileUpload = React.useCallback(
    async (file: File, fileType: FileType) => {
      const messageTime = dayjs.utc().format();
      const dataUrl = URL.createObjectURL(file);

      if (file && conversation) {
        const newMessages = [
          {
            __typename: 'ChatMessageItem',
            fromUserId: venueID,
            toUserId: conversation.toUserId, // adminID
            conversationId: conversation.conversationId,
            userType: 'venue/user',
            messageTime: messageTime,
            message: dataUrl,
            messageType: fileType,
          },
          ...messages,
        ];
        setMessages(newMessages as ChatMessageItem[]);

        const url = await uploadFileToS3(file);
        setIsFileUploading(false);
        const [, toUserId] = conversation.toUserId.split('-');
        if (venueID && conversation) {
          await postMessageMutation.mutateAsync({
            conversationId: conversation.conversationId,
            message: url,
            fromUserChatId: venueID,
            toUserChatId: toUserId,
            messageType: fileType,
            reservationID: reservationDetail?.id || '',
          });
        }
      }
    },
    [conversation, messages, postMessageMutation, reservationDetail?.id, venueID],
  );

  const messageSubmitHandler = async () => {
    if (newMessage.trim() !== '') {
      const _newMessage = {
        __typename: 'ChatMessageItem',
        fromUserId: conversation.fromUserId,
        toUserId: conversation.toUserId,
        conversationId: conversation.conversationId,
        userType: 'venue/user',
        messageTime: dayjs.utc().format(),
        message: newMessage,
      };

      const messageToSend = newMessage;
      setMessages((prev) => [_newMessage, ...prev] as ChatMessageItem[]);
      setNewMessage('');
      requestAnimationFrame(() => {
        scrollingView.current?.scrollIntoView({ block: 'end', behavior: 'auto' });
      });
      const [, toUserId] = conversation.toUserId.split('-');
      try {
        await postMessageMutation.mutateAsync({
          conversationId: conversation.conversationId,
          message: messageToSend,
          fromUserChatId: venueID || '',
          toUserChatId: toUserId,
          reservationID: reservationDetail?.id || '',
        });
      } catch {
        // Backend rejected the message: roll back the optimistic bubble and
        // restore the input so the user doesn't lose their text.
        // (the mutation's onError already surfaces the error snackbar)
        setMessages((prev) =>
          prev.filter((m) => m?.messageTime !== _newMessage.messageTime),
        );
        setNewMessage(messageToSend);
      }
      setTimeout(() => {
        focusInput();
      }, 0);
    }
  };

  const isChatEnd = !!conversation.isChatClosed;

  return (
    <>
      <ChatHeader
        conversation={conversation}
        reservationDetail={reservationDetail}
        reservationDetailsRefetch={() => {
          reservationDetailsRefetch();
        }}
      />
      <Box
        sx={{
          flexGrow: 1,
          width: '100%',
          height: 'calc(100vh - 164px)',
          overflow: 'auto',
          bgcolor: '#FCFCFC',
          position: 'relative',
        }}
      >
        <Box position={'absolute'} top={0} right={0} left={0} zIndex={99}>
          <ViolationMessage />
        </Box>
        <Box
          sx={{
            textAlign: 'left',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '0px',
            justifyContent: 'end',
          }}
        >
          {!isLocalMessageStart && (isFetchingMessages || isLoadingMessages) && (
            <Box textAlign={'center'} marginTop={8}>
              <CircularProgress style={{ width: 20, height: 20 }} />
              <div>
                <em>Loading messages...</em>
              </div>
            </Box>
          )}
          <Gallery>
            <Box
              sx={{
                flex: '0 1 85%',
                height: '100%',
                overflowY: 'auto',
                maxHeight: '100%',
                padding: '32px',
                borderRadius: '5px',
                display: 'flex',
                flexDirection: 'column-reverse',
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f1f1f1',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#a8a8a8',
                  borderRadius: '5px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: '#111',
                },
              }}
            >
              {isChatEnd && (
                <Box sx={{ px: 1, my: 2, display: 'flex', alignItems: 'center' }}>
                  <LineCloseChat />
                  <ChatClosedMessage>Chat closed</ChatClosedMessage>
                  <LineCloseChat />
                </Box>
              )}
              <div ref={scrollingView} />
              {messages.map((message, index) => {
                if (!message) return null;
                const olderMessage = messages[index + 1];
                const startsNewDay =
                  !olderMessage ||
                  !dayjs(message.messageTime).isSame(dayjs(olderMessage.messageTime), 'day');
                return (
                  <React.Fragment key={message.messageTime}>
                    <MessageV2
                      userId={venueID || ''}
                      conversation={conversation}
                      index={index}
                      lastElementRef={lastElementRef}
                      messages={messages}
                      msg={message}
                    />
                    {startsNewDay && <DateDivider date={message.messageTime} />}
                  </React.Fragment>
                );
              })}
              <div className='message-content-receiver-online-brand'>
                <Box display={'flex'} flexDirection={'column'} gap={'20px'}>
                  {reservationDetail && <ReservationRequirement reservation={reservationDetail} />}
                </Box>
              </div>
            </Box>
          </Gallery>
          <Box
            sx={{
              backgroundColor: '#fff',
              borderRadius: '28px',
              boxShadow: '0 4px 18px rgba(17, 17, 17, 0.10)',
              margin: '12px 20px 18px',
              padding: '8px 12px',
              display: 'flex',
              alignItems: 'center',
              position: 'relative',
              zIndex: 2,
              gap: '12px',
            }}
          >
            <AttachmentInput
              handleFileUpload={handleFileUpload}
              disabled={isFileUploading || isChatEnd}
            />
            <StyledInput
              placeholder='Type your message'
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              ref={messageInputRef}
              minRows={1}
              maxRows={15}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.shiftKey == false) {
                  e.preventDefault();
                  messageSubmitHandler();
                }
              }}
              disabled={
                isChatEnd ||
                reservationDetail?.shippingStatus === ShippingStatus.cancelled ||
                postMessageMutation.isPending
              }
            />
            <ButtonContainer
              type='button'
              disabled={isChatEnd}
              $active={newMessage.trim().length > 0}
              onClick={messageSubmitHandler}
            >
              <Icons.SendIcon />
            </ButtonContainer>
          </Box>
        </Box>
      </Box>
    </>
  );
};

const DateDivider: React.FC<{ date?: string | null }> = ({ date }) => {
  if (!date) return null;
  const d = dayjs(date);
  const label = d.isSame(dayjs(), 'day')
    ? 'Today'
    : d.isSame(dayjs().subtract(1, 'day'), 'day')
      ? 'Yesterday'
      : d.format('MMMM D, YYYY');

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        my: '16px',
      }}
    >
      <DateDividerLabel>{label}</DateDividerLabel>
    </Box>
  );
};

const DateDividerLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #949ba5;
  background: #f4f4f5;
  padding: 4px 12px;
  border-radius: 999px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const StyledInput = styled(TextareaAutosize)`
  background-color: transparent;
  width: 100%;
  font-size: 15px;
  outline: none;
  border: none;
  padding: 16px 8px;
  resize: none;

  &:focus {
    outline: none;
  }
  @media (max-width: 485px) {
    padding: 8px 6px;
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const ButtonContainer = styled.button<{ $active?: boolean }>`
  border: none;
  background-color: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  color: ${(props) => (props.$active ? '#765BC6' : '#949ba5')};
  transition: color 0.15s ease;

  &:disabled {
    cursor: not-allowed;
  }
`;

const ChatClosedMessage = styled.span`
  font-size: 10px;
  color: #949ba5;
  font-weight: 600;
  text-transform: uppercase;
  min-width: 70px;
  display: block;
  margin: 0 24px;
`;

const LineCloseChat = styled.div`
  height: 1px;
  background: #949ba5;
  width: 100%;
`;
